"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger, ContextMenuLabel, ContextMenuSeparator, ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuPortal } from "@/components/ui/context-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRouter } from "next/navigation";
import { Settings, Wrench, Users, User, Zap, Cpu, Database, Cloud, Sparkles, BrainCircuit, Bot, RefreshCw, Copy, CheckCircle2, Globe, Radio, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStore } from "@/lib/state/store";
import { useServerTiers } from "@/lib/hooks/useServerTiers";

/**
 * Properties for the {@link HeatherContextMenu} component.
 */
interface HeatherContextMenuProps {
  children: React.ReactNode;
  variant?: "business" | "personal";
  partner?: string;
  onOpenChat?: (tab: string, partner?: string) => void;
  onClick?: () => void;
  onOpenWorkOrders?: (mode?: 'list' | 'new') => void;
  onHardResetSession?: () => void;
}

/**
 * Reusable context menu for Heather and RAM Server sync badges.
 * Includes Comms, Work Orders, and Multi-Tier Server Telemetry.
 */
export const HeatherContextMenu = React.forwardRef<
  HTMLDivElement,
  HeatherContextMenuProps & React.HTMLAttributes<HTMLDivElement>
>(({ children, variant = "business", partner, onOpenChat, onClick, onOpenWorkOrders, onHardResetSession, ...props }, ref) => {
  const router = useRouter();
  const isPersonal = variant === "personal";
  const { brainMode, setBrainMode, currentUser } = useStore();
  const { heather, ram, isChecking, refresh } = useServerTiers();
  const [copied, setCopied] = useState(false);

  const handleChat = (tab: string, targetPartner?: string) => {
    if (onOpenChat) {
      onOpenChat(tab, targetPartner);
    } else if (onClick) {
      onClick();
    } else {
      router.push('/heather');
    }
  };

  const handleCopyUrl = (url: string | null) => {
    if (url && typeof navigator !== 'undefined') {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const isIndividualPartner = partner && partner !== 'Heather';

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div ref={ref} {...props} className={cn("inline-block", props.className)}>
          {children}
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className={cn(
        "w-64 text-white rounded-xl overflow-hidden py-1 z-[9900]",
        isPersonal
          ? "bg-black/95 border-fuchsia-500/20 backdrop-blur-xl shadow-[0_8px_32px_rgba(217,70,239,0.15)]"
          : "bg-zinc-950/95 border-white/10 shadow-2xl backdrop-blur-xl"
      )}>

        {/* Section Header */}
        <ContextMenuLabel className="text-[10px] uppercase font-black tracking-widest text-white/40 px-3 py-1.5 flex items-center justify-between">
          <span>{partner ? `${partner.toUpperCase()} COMMS` : "TELEMETRY & COMMS"}</span>
          <span className="text-[9px] text-emerald-400 font-mono">RAM-OS</span>
        </ContextMenuLabel>

        {/* Multi-Tier Connection Status Indicators */}
        <ContextMenuSub>
          <ContextMenuSubTrigger className="gap-2.5 cursor-pointer focus:bg-white/10 py-2 text-xs">
            <Radio className="h-4 w-4 text-cyan-400 shrink-0" />
            <div className="flex items-center justify-between flex-1 pr-1">
              <span>Server Tiers</span>
              <div className="flex items-center gap-1">
                <span className={cn("text-[9px] font-mono px-1.5 py-0.5 rounded", heather.tier === 'tier1_cloud' ? "bg-emerald-500/20 text-emerald-400" : heather.tier === 'tier2_funnel' ? "bg-amber-500/20 text-amber-400" : heather.tier === 'tier3_local' ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400")}>
                  H:{heather.shortLabel}
                </span>
                <span className={cn("text-[9px] font-mono px-1.5 py-0.5 rounded", ram.tier === 'tier1_cloud' ? "bg-emerald-500/20 text-emerald-400" : ram.tier === 'tier2_funnel' ? "bg-amber-500/20 text-amber-400" : ram.tier === 'tier3_local' ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400")}>
                  RAM:{ram.shortLabel}
                </span>
              </div>
            </div>
          </ContextMenuSubTrigger>
          <ContextMenuPortal>
            <ContextMenuSubContent className="text-white rounded-xl z-[9990] bg-zinc-950 border-white/10 p-2.5 min-w-[240px] space-y-2.5 shadow-2xl">
              {/* Heather Engine */}
              <div>
                <div className="text-[10px] uppercase tracking-wider font-black text-cyan-400 px-1 pb-1 flex items-center justify-between border-b border-white/10">
                  <span>Heather Engine</span>
                  {heather.latencyMs !== null && <span className="font-mono text-[9px] text-white/50">{heather.latencyMs}ms</span>}
                </div>
                <div className="space-y-1 text-xs mt-1.5">
                  <div className={cn("flex items-center justify-between px-1.5 py-1 rounded", heather.tier === 'tier1_cloud' ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-white/60")}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      <span className="font-semibold text-[11px]">Tier 1: Cloud (Render)</span>
                    </div>
                    {heather.tier === 'tier1_cloud' && <span className="text-[9px] font-mono font-bold text-emerald-400">ACTIVE</span>}
                  </div>
                  <div className={cn("flex items-center justify-between px-1.5 py-1 rounded", heather.tier === 'tier2_funnel' ? "bg-amber-500/20 text-amber-300" : "bg-white/5 text-white/60")}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-amber-400" />
                      <span className="font-semibold text-[11px]">Tier 2: Funnel</span>
                    </div>
                    {heather.tier === 'tier2_funnel' && <span className="text-[9px] font-mono font-bold text-amber-400">ACTIVE</span>}
                  </div>
                  <div className={cn("flex items-center justify-between px-1.5 py-1 rounded", heather.tier === 'tier3_local' ? "bg-orange-500/20 text-orange-300" : "bg-white/5 text-white/60")}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-orange-400" />
                      <span className="font-semibold text-[11px]">Tier 3: Localhost</span>
                    </div>
                    {heather.tier === 'tier3_local' && <span className="text-[9px] font-mono font-bold text-orange-400">ACTIVE</span>}
                  </div>
                </div>
              </div>

              {/* RAM Server / App Engine */}
              <div>
                <div className="text-[10px] uppercase tracking-wider font-black text-emerald-400 px-1 pb-1 flex items-center justify-between border-b border-white/10">
                  <span>RAM App Platform</span>
                  {ram.latencyMs !== null && <span className="font-mono text-[9px] text-white/50">{ram.latencyMs}ms</span>}
                </div>
                <div className="space-y-1 text-xs mt-1.5">
                  <div className={cn("flex items-center justify-between px-1.5 py-1 rounded", ram.tier === 'tier1_cloud' ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-white/60")}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />
                      <span className="font-semibold text-[11px]">Tier 1: Cloud Web</span>
                    </div>
                    {ram.tier === 'tier1_cloud' && <span className="text-[9px] font-mono font-bold text-emerald-400">ACTIVE</span>}
                  </div>
                  <div className={cn("flex items-center justify-between px-1.5 py-1 rounded", ram.tier === 'tier2_funnel' ? "bg-amber-500/20 text-amber-300" : "bg-white/5 text-white/60")}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-amber-400" />
                      <span className="font-semibold text-[11px]">Tier 2: Tailscale Mesh</span>
                    </div>
                    {ram.tier === 'tier2_funnel' && <span className="text-[9px] font-mono font-bold text-amber-400">ACTIVE</span>}
                  </div>
                  <div className={cn("flex items-center justify-between px-1.5 py-1 rounded", ram.tier === 'tier3_local' ? "bg-orange-500/20 text-orange-300" : "bg-white/5 text-white/60")}>
                    <div className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-orange-400" />
                      <span className="font-semibold text-[11px]">Tier 3: Local Dev/Rig</span>
                    </div>
                    {ram.tier === 'tier3_local' && <span className="text-[9px] font-mono font-bold text-orange-400">ACTIVE</span>}
                  </div>
                </div>
              </div>

              <ContextMenuSeparator className="bg-white/10 my-1" />

              <ContextMenuItem
                className="cursor-pointer focus:bg-white/10 text-[11px] gap-2 py-1.5"
                onClick={() => void refresh()}
              >
                <RefreshCw className={cn("h-3.5 w-3.5 text-cyan-400", isChecking && "animate-spin")} />
                <span>{isChecking ? "Pinging Tiers..." : "Refresh Server Ping"}</span>
              </ContextMenuItem>

              {heather.url && (
                <ContextMenuItem
                  className="cursor-pointer focus:bg-white/10 text-[11px] gap-2 py-1.5"
                  onClick={() => handleCopyUrl(heather.url)}
                >
                  {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5 text-white/50" />}
                  <span>{copied ? "Copied URL!" : "Copy Heather Backend URL"}</span>
                </ContextMenuItem>
              )}

              {ram.url && (
                <ContextMenuItem
                  className="cursor-pointer focus:bg-white/10 text-[11px] gap-2 py-1.5"
                  onClick={() => handleCopyUrl(ram.url)}
                >
                  {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <Globe className="h-3.5 w-3.5 text-white/50" />}
                  <span>Copy RAM App Origin</span>
                </ContextMenuItem>
              )}
            </ContextMenuSubContent>
          </ContextMenuPortal>
        </ContextMenuSub>

        <ContextMenuSeparator className="bg-white/10" />

        {/* Direct Chat (if right-clicking a specific partner) */}
        {isIndividualPartner && (
          <ContextMenuItem
            className="cursor-pointer focus:bg-white/10 text-xs gap-2.5 py-2 font-semibold text-emerald-400 focus:text-emerald-300"
            onClick={() => handleChat('partner-direct', partner)}
          >
            <User className="h-4 w-4 shrink-0 text-emerald-400" />
            <span>Direct Chat with {partner}</span>
          </ContextMenuItem>
        )}

        {/* Primary Comms Options */}
        <ContextMenuItem
          className="cursor-pointer focus:bg-white/10 text-xs gap-2.5 py-2"
          onClick={() => handleChat('team')}
        >
          <Users className="h-4 w-4 shrink-0 text-indigo-400" />
          <span>Team Chat</span>
        </ContextMenuItem>

        <ContextMenuItem
          className="cursor-pointer focus:bg-white/10 text-xs gap-2.5 py-2 text-cyan-400 focus:text-cyan-300"
          onClick={() => handleChat('heather')}
        >
          <Bot className="h-4 w-4 shrink-0 text-cyan-400" />
          <span>Heather Intelligence Chat</span>
        </ContextMenuItem>

        <ContextMenuItem
          className="cursor-pointer focus:bg-white/10 text-xs gap-2.5 py-2"
          onClick={() => handleChat('hybrid')}
        >
          <BrainCircuit className="h-4 w-4 shrink-0 text-amber-400" />
          <span>Group Channel (Crew + Heather)</span>
        </ContextMenuItem>

        <ContextMenuSeparator className="bg-white/10" />

        {/* Heather Mission Control / 3D Office */}
        <ContextMenuItem
          className="cursor-pointer focus:bg-white/10 text-xs gap-2.5 py-2 text-cyan-300 font-semibold"
          onClick={() => router.push('/heather/office')}
        >
          <Sparkles className="h-4 w-4 shrink-0 text-cyan-400" />
          <span>Heather&apos;s Office (3D Stage)</span>
        </ContextMenuItem>

        <ContextMenuItem
          className="cursor-pointer focus:bg-white/10 text-xs gap-2.5 py-2 text-white/80"
          onClick={() => router.push('/heather/memory')}
        >
          <Database className="h-4 w-4 shrink-0 text-purple-400" />
          <span>Memory Core</span>
        </ContextMenuItem>

        <ContextMenuSeparator className="bg-white/10" />

        {/* Brain Mode Submenu */}
        <ContextMenuSub>
          <ContextMenuSubTrigger className="gap-2.5 cursor-pointer focus:bg-white/10 py-2 text-xs">
            <Cpu className={cn("h-4 w-4 shrink-0", isPersonal ? "text-cyan-400" : "text-yellow-400")} />
            <span className="font-semibold">Brain Mode ({brainMode})</span>
          </ContextMenuSubTrigger>
          <ContextMenuPortal>
            <ContextMenuSubContent className={cn(
              "text-white rounded-xl z-[9990]",
              isPersonal ? "bg-black/95 border-white/10 backdrop-blur-xl" : "bg-zinc-950 border-white/10"
            )}>
              <ContextMenuItem
                className={cn(
                  "cursor-pointer focus:bg-white/10 text-xs gap-2 flex items-center justify-between",
                  brainMode === "Auto" && "bg-white/10 text-[#c1ff00]"
                )}
                onClick={() => setBrainMode("Auto")}
              >
                <span className="flex items-center gap-2">
                  <Zap className="h-3.5 w-3.5 text-yellow-400" /> Auto (Cascading)
                </span>
                {brainMode === "Auto" && <span className="h-1.5 w-1.5 rounded-full bg-[#c1ff00]" />}
              </ContextMenuItem>
              <ContextMenuItem
                className={cn(
                  "cursor-pointer focus:bg-white/10 text-xs gap-2 flex items-center justify-between",
                  brainMode === "Cloud" && "bg-white/10 text-[#c1ff00]"
                )}
                onClick={() => setBrainMode("Cloud")}
              >
                <span className="flex items-center gap-2">
                  <Cloud className="h-3.5 w-3.5 text-blue-400" /> Cloud (Gemini)
                </span>
                {brainMode === "Cloud" && <span className="h-1.5 w-1.5 rounded-full bg-[#c1ff00]" />}
              </ContextMenuItem>
              <ContextMenuItem
                className={cn(
                  "cursor-pointer focus:bg-white/10 text-xs gap-2 flex items-center justify-between",
                  brainMode === "Local" && "bg-white/10 text-[#c1ff00]"
                )}
                onClick={() => setBrainMode("Local")}
              >
                <span className="flex items-center gap-2">
                  <Database className="h-3.5 w-3.5 text-emerald-400" /> Local (Ollama)
                </span>
                {brainMode === "Local" && <span className="h-1.5 w-1.5 rounded-full bg-[#c1ff00]" />}
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuPortal>
        </ContextMenuSub>

        {onOpenWorkOrders && (
          <>
            <ContextMenuSeparator className="bg-white/10" />
            <ContextMenuItem
              onClick={() => onOpenWorkOrders('list')}
              className="gap-2.5 cursor-pointer py-2 focus:bg-white/10 text-xs font-semibold text-orange-400 hover:text-orange-300"
            >
              <Wrench className="h-4 w-4 shrink-0 text-orange-400" />
              <span>Open Work Orders</span>
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => onOpenWorkOrders('new')}
              className="gap-2.5 cursor-pointer py-2 focus:bg-white/10 text-xs font-semibold text-emerald-400 hover:text-emerald-300"
            >
              <Zap className="h-4 w-4 shrink-0 text-emerald-400" />
              <span>New Work Order Setup</span>
            </ContextMenuItem>
          </>
        )}

        {onHardResetSession && (
          <>
            <ContextMenuSeparator className="bg-white/10" />
            <ContextMenuItem
              onClick={onHardResetSession}
              className="gap-2.5 cursor-pointer focus:bg-rose-500/20 py-2 text-xs font-semibold text-rose-400 hover:text-rose-300"
            >
              <Trash2 className="h-4 w-4 shrink-0 text-rose-400" />
              <span>Hard Reset Chat Session</span>
            </ContextMenuItem>
          </>
        )}

        <ContextMenuSeparator className="bg-white/10" />

        <ContextMenuItem
          onClick={() => router.push('/dashboard/settings')}
          className="gap-2.5 cursor-pointer focus:bg-white/10 py-2 text-xs text-white/70 hover:text-white"
        >
          <Settings className="h-4 w-4 shrink-0 text-white/50" />
          <span>System Settings</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
});
HeatherContextMenu.displayName = "HeatherContextMenu";

/**
 * Properties for the {@link HeatherSyncBadge} component.
 */
interface HeatherSyncBadgeProps {
  variant?: "business" | "personal";
  className?: string;
  onClick?: () => void;
  onOpenWorkOrders?: (mode?: 'list' | 'new') => void;
}

/**
 * Status badge showing real-time sync state with Heather.
 * Displays Green for Tier 1 Cloud, Yellow for Tier 2 Funnel, Orange for Tier 3 Localhost, and Red for Offline.
 */
export function HeatherSyncBadge({ variant = "business", className, onClick, onOpenWorkOrders }: HeatherSyncBadgeProps) {
  const { heather } = useServerTiers();

  const badgeObj = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          onClick={onClick}
          className={cn(
            "h-6 gap-1.5 px-2 py-1 text-[10px] tracking-widest font-black shadow-lg transition-all cursor-pointer uppercase select-none flex items-center",
            heather.badgeClass,
            className
          )}
        >
          <Avatar className="h-3.5 w-3.5 ring-1 ring-white/30 shrink-0">
            <AvatarImage src="/icons/heather/icon1.png" className="object-cover" />
            <AvatarFallback className="text-[7px] bg-black font-black">H</AvatarFallback>
          </Avatar>
          <span>HEATHER</span>
          <span className="text-[8px] opacity-75 font-mono px-1 rounded bg-black/20">
            {heather.shortLabel}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="text-[10px] font-bold p-3 space-y-2 min-w-[200px] bg-zinc-950 border-white/10 shadow-2xl">
        <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-1.5">
          <span className="text-muted-foreground uppercase">Heather Intelligence</span>
          <span className={cn("px-1.5 py-0.5 rounded text-[9px] font-mono", heather.tier === 'tier1_cloud' ? "bg-emerald-500/20 text-emerald-400" : heather.tier === 'tier2_funnel' ? "bg-amber-500/20 text-amber-400" : heather.tier === 'tier3_local' ? "bg-orange-500/20 text-orange-400" : "bg-red-500/20 text-red-400")}>
            {heather.name}
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-muted-foreground uppercase">Active Host</span>
          <span className="font-mono text-[9px] truncate max-w-[130px]">{heather.url || 'None'}</span>
        </div>
        {heather.latencyMs !== null && (
          <div className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground uppercase">Ping Latency</span>
            <span className="font-mono text-emerald-400">{heather.latencyMs} ms</span>
          </div>
        )}
        <div className="text-[8px] text-muted-foreground border-t border-white/10 pt-1.5 text-center leading-relaxed">
          Automatic Failover: Cloud (Tier 1) $\rightarrow$ Funnel (Tier 2) $\rightarrow$ Local (Tier 3)<br />
          <span className="text-white/60">Click to Chat • Right-click for Telemetry</span>
        </div>
      </TooltipContent>
    </Tooltip>
  );

  const innerTrigger = onClick ? (
    <div tabIndex={0} role="button">{badgeObj}</div>
  ) : (
    <Link href="/heather">{badgeObj}</Link>
  );

  return (
    <HeatherContextMenu variant={variant} onClick={onClick} onOpenWorkOrders={onOpenWorkOrders}>
      {innerTrigger}
    </HeatherContextMenu>
  );
}
